import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { mcGetTokenFirstBuys } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Skeleton } from './ui/skeleton';
import { Badge } from './ui/badge';
import { Search, Loader2, ArrowRight, Info } from 'lucide-react';

const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '—');

export default function McFirstBuys({ chain, chainInfo }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [token, setToken] = useState(searchParams.get('token') || '');
  const [data, setData] = useState(null);
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const label = chainInfo?.label || chain;

  const load = useCallback(async (addr) => {
    const a = (addr || '').trim();
    if (!a) return;
    setLoading(true); setError(null);
    try {
      const d = await mcGetTokenFirstBuys(chain, a, 500);
      setData(d);
      const pts = (d?.points || []).map((p) => ({
        ...p,
        ts: new Date(p.first_buy_time).getTime(),
        priceMicros: (p.avg_buy_price_usd || 0) * 1e6,
        size: Math.max(20, Math.min(300, Math.log(1 + (p.total_bought_usd || 0)) * 30)),
      }));
      setPoints(pts);
      if ((d?.points || []).length === 0) setError('No recent buys found in the available trade window for this token.');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load first buys');
      setData(null); setPoints([]);
    } finally {
      setLoading(false);
    }
  }, [chain]);

  useEffect(() => {
    const t = searchParams.get('token');
    if (t) { setToken(t); load(t); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chain]);

  const submit = (e) => {
    e.preventDefault();
    const a = token.trim();
    const p = new URLSearchParams(searchParams);
    if (a) p.set('token', a); else p.delete('token');
    setSearchParams(p, { replace: true });
    load(a);
  };

  const formatTs = (ts) => {
    const d = new Date(ts);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  return (
    <div className="space-y-6" data-testid="mc-first-buys">
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2" data-testid="mc-first-buys-form">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="mc-first-buys-input"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder={`Token contract on ${label}…`}
            className="pl-10 font-mono text-sm h-11"
          />
        </div>
        <Button type="submit" className="h-11 px-6" disabled={loading || !token.trim()} data-testid="mc-first-buys-load-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Plot <ArrowRight className="w-4 h-4 ml-1" /></>}
        </Button>
      </form>

      <div className="flex items-start gap-2 text-xs text-warning border border-warning/30 bg-warning/5 p-3 rounded" data-testid="mc-first-buys-approx-note">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          On {label} we don't run a full historical indexer, so this plots <b>recent buyers</b> from the latest
          available pool trades (GeckoTerminal window) — not the token's entire history. For a complete
          first-buy timeline, use the Krown chain.
        </span>
      </div>

      {error && (
        <div className="border border-destructive/40 bg-destructive/10 text-destructive text-sm p-3 rounded" data-testid="mc-first-buys-error">
          {error}
        </div>
      )}

      <Card>
        <CardHeader className="pb-3 border-b border-border">
          <CardTitle className="text-sm flex items-center gap-3 flex-wrap">
            <span>Recent buyers scatter</span>
            {data?.symbol && <Badge variant="outline" className="text-[10px]">{data.symbol}</Badge>}
            {data?.window && (
              <span className="text-[10px] text-muted-foreground font-normal">
                {new Date(data.window.from).toLocaleString()} → {new Date(data.window.to).toLocaleString()}
              </span>
            )}
            {data && (
              <span className="ml-auto text-[10px] text-muted-foreground font-normal" data-testid="mc-first-buys-scanned">
                {data.trades_scanned || 0} trades scanned
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {loading ? (
            <Skeleton className="h-[460px] w-full" />
          ) : points.length === 0 ? (
            <p className="text-sm text-muted-foreground py-12 text-center" data-testid="mc-first-buys-empty">
              Paste a token contract to plot its recent buyers on {label}.
            </p>
          ) : (
            <div data-testid="mc-first-buys-chart">
              <ResponsiveContainer width="100%" height={460}>
                <ScatterChart margin={{ top: 10, right: 20, bottom: 40, left: 55 }}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="ts" type="number" name="First buy"
                    domain={['dataMin', 'dataMax']}
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                    tickFormatter={(v) => new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    label={{ value: 'First buy (in window)', position: 'bottom', offset: 25, fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis
                    dataKey="priceMicros" type="number" name="Avg buy price"
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                    tickFormatter={(v) => `$${(v / 1e6).toFixed(6)}`}
                    label={{ value: 'Avg buy price (USD)', angle: -90, position: 'left', offset: 12, fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <ZAxis dataKey="size" range={[20, 300]} />
                  <Tooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload;
                      return (
                        <div className="bg-popover border border-border rounded-md px-3 py-2 text-xs text-foreground shadow-xl">
                          <div className="font-mono">{shortAddr(p.wallet)}</div>
                          <div>First: <span className="text-primary">{formatTs(p.ts)}</span></div>
                          <div>Avg price: <span className="text-primary">${(p.avg_buy_price_usd || 0).toFixed(8)}</span></div>
                          <div>Bought: <span className="text-primary">${(p.total_bought_usd || 0).toFixed(2)}</span> ({p.buys} buys)</div>
                        </div>
                      );
                    }}
                  />
                  <Scatter
                    data={points}
                    fill="hsl(var(--primary))"
                    fillOpacity={0.6}
                    onClick={(d) => { if (d?.wallet) window.location.href = `/explorer/${chain}/address/${d.wallet}`; }}
                    style={{ cursor: 'pointer' }}
                  />
                </ScatterChart>
              </ResponsiveContainer>
              <p className="text-xs text-muted-foreground mt-3">
                Showing <b>{points.length}</b> recent buyers. Click any dot to open the wallet.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
